import { z } from "zod";
import {
    agentChatAssistantMessageSchema,
    agentChatAttachmentInputSchema,
    agentChatIntentEnum,
    toolTraceEntrySchema,
} from "./agent-chat.schema";

// Shared request body for both the ts-rest `sendMessage` route and the raw
// SSE-over-POST stream route, so both validate the payload identically.
export const sendMessageBodySchema = z.object({
    workspaceId: z.string().uuid(),
    conversationId: z.string().uuid().nullable(),
    message: z.string().min(1).max(8000),
    attachments: z
        .array(agentChatAttachmentInputSchema)
        .max(5)
        .refine(
            (items) =>
                new Set(items.map((i) => i.financialDocumentId)).size ===
                items.length,
            {
                message:
                    "attachments must reference distinct financialDocumentId values",
            },
        )
        .optional(),
});
export type SendMessageBody = z.infer<typeof sendMessageBodySchema>;

export const AgentChatStreamEventName = {
    INTENT_DETECTED: "intent_detected",
    TOOL_STARTED: "tool_started",
    TOOL_COMPLETED: "tool_completed",
    MESSAGE_COMPLETE: "message_complete",
    ERROR: "error",
} as const;

export type AgentChatStreamEventName =
    (typeof AgentChatStreamEventName)[keyof typeof AgentChatStreamEventName];

const intentDetectedPayloadSchema = z.object({
    resolvedIntent: agentChatIntentEnum,
    reasoning: z.string(),
    toolCandidates: z.array(
        z.object({
            name: z.string(),
            probability: z.number().min(0).max(1),
        }),
    ),
    skillCandidates: z.array(z.object({ name: z.string() })).optional(),
});

const toolStartedPayloadSchema = z.object({
    traceIndex: z.number().int().nonnegative(),
    name: z.string(),
    input: z.record(z.unknown()),
});

const toolCompletedPayloadSchema = z.object({
    traceIndex: z.number().int().nonnegative(),
    entry: toolTraceEntrySchema,
});

const messageCompletePayloadSchema = z.object({
    conversationId: z.string().uuid(),
    assistantMessage: agentChatAssistantMessageSchema,
});

const errorPayloadSchema = z.object({
    message: z.string(),
});

const agentChatStreamEventEnvelopeSchema = <
    EventName extends AgentChatStreamEventName,
    PayloadSchema extends z.ZodType,
>(
    event: EventName,
    payload: PayloadSchema,
) => z.object({ event: z.literal(event), payload }).strict();

export const agentChatStreamEventSchema = z.discriminatedUnion("event", [
    agentChatStreamEventEnvelopeSchema(
        AgentChatStreamEventName.INTENT_DETECTED,
        intentDetectedPayloadSchema,
    ),
    agentChatStreamEventEnvelopeSchema(
        AgentChatStreamEventName.TOOL_STARTED,
        toolStartedPayloadSchema,
    ),
    agentChatStreamEventEnvelopeSchema(
        AgentChatStreamEventName.TOOL_COMPLETED,
        toolCompletedPayloadSchema,
    ),
    agentChatStreamEventEnvelopeSchema(
        AgentChatStreamEventName.MESSAGE_COMPLETE,
        messageCompletePayloadSchema,
    ),
    agentChatStreamEventEnvelopeSchema(
        AgentChatStreamEventName.ERROR,
        errorPayloadSchema,
    ),
]);
export type AgentChatStreamEvent = z.infer<typeof agentChatStreamEventSchema>;
